import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, FileSearch, Hand, Scale } from 'lucide-react'
import { api, messageErreur } from '../../lib/api'
import type { Conflit, Donnee } from '../../lib/types'
import { Champ, Message, Modale } from '../base'
import VisionneuseDocument from '../VisionneuseDocument'
import * as f from '../../lib/format'
import { detailLisible } from '../../lib/resume'
import { CATEGORIES_DONNEE, DECISIONS_CONFLIT } from '@doc/shared'

/**
 * Un désaccord entre deux informations n'est jamais tranché par l'application :
 * les deux valeurs sont montrées avec leurs dates et leurs sources, et la
 * décision du médecin est enregistrée (critère d'acceptation 4).
 */
export default function Conflits({
  conflits,
  patientId,
  conflitDemande,
}: {
  conflits: Conflit[]
  patientId: string
  conflitDemande?: string | null
}) {
  const [ouvert, setOuvert] = useState<Conflit | null>(
    conflitDemande ? (conflits.find((c) => c.id === conflitDemande) ?? null) : null,
  )

  if (conflits.length === 0) return null

  return (
    <div className="mb-5 overflow-hidden rounded-[14px] bg-critique-fond">
      <div className="flex items-center gap-2 px-5 pt-4 pb-2">
        <AlertTriangle className="h-[18px] w-[18px] text-critique" />
        <h2 className="text-[15px] font-semibold text-critique">
          {conflits.length === 1
            ? 'Un désaccord à trancher'
            : `${conflits.length} désaccords à trancher`}
        </h2>
      </div>
      <p className="px-5 pb-3 text-[13px] text-encre">
        Une information du dossier est contredite. Rien n’a été modifié : la décision t’appartient.
      </p>

      <ul className="flex flex-col gap-2 px-5 pb-5">
        {conflits.map((conflit) => (
          <li key={conflit.id} className="rounded-xl bg-white px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="text-[12px] text-brume">
                  {CATEGORIES_DONNEE[conflit.existante.categorie]}
                </span>
                <p className="text-[14px]">
                  « {conflit.existante.libelle} » contre « {conflit.proposee.libelle} »
                </p>
                {conflit.explication && (
                  <p className="mt-0.5 text-[12.5px] text-ardoise">{conflit.explication}</p>
                )}
              </div>
              <button className="bouton-primaire shrink-0" onClick={() => setOuvert(conflit)}>
                <Scale className="h-4 w-4" />
                Trancher
              </button>
            </div>
          </li>
        ))}
      </ul>

      {ouvert && (
        <Decision conflit={ouvert} patientId={patientId} fermer={() => setOuvert(null)} />
      )}
    </div>
  )
}

function Decision({
  conflit,
  patientId,
  fermer,
}: {
  conflit: Conflit
  patientId: string
  fermer: () => void
}) {
  const client = useQueryClient()
  const [decision, setDecision] = useState<keyof typeof DECISIONS_CONFLIT>('GARDER_EXISTANT')
  const [commentaire, setCommentaire] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [source, setSource] = useState<{ id: string; page: number | null; passage: string | null } | null>(
    null,
  )

  const trancher = useMutation({
    mutationFn: () =>
      api(`/api/conflits/${conflit.id}/decision`, {
        methode: 'POST',
        corps: { decision, commentaire: commentaire || undefined },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      client.invalidateQueries({ queryKey: ['accueil'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre="Trancher le désaccord"
      sousTitre="Les deux valeurs, leurs dates et leurs sources. Ta décision et les versions antérieures sont conservées."
      large
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => trancher.mutate()}
            disabled={trancher.isPending}
          >
            Enregistrer la décision
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Cote titre="Dans le dossier" donnee={conflit.existante} surSource={setSource} />
          <Cote titre="Nouvelle information" donnee={conflit.proposee} surSource={setSource} />
        </div>

        <Champ etiquette="Décision">
          <div className="flex flex-col gap-1.5 pt-1">
            {Object.entries(DECISIONS_CONFLIT).map(([cle, libelle]) => (
              <label key={cle} className="flex items-start gap-2 text-[13.5px]">
                <input
                  type="radio"
                  name="decision"
                  className="mt-1 h-4 w-4 accent-[var(--color-bleu-600)]"
                  checked={decision === cle}
                  onChange={() => setDecision(cle as keyof typeof DECISIONS_CONFLIT)}
                />
                <span>
                  {libelle}
                  <span className="block text-[12px] text-brume">{EXPLICATIONS[cle]}</span>
                </span>
              </label>
            ))}
          </div>
        </Champ>

        <Champ etiquette="Commentaire" aide="Conservé avec la décision.">
          <input
            className="champ"
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
          />
        </Champ>

        {erreur && <Message ton="critique">{erreur}</Message>}
      </div>

      {source && (
        <VisionneuseDocument
          documentId={source.id}
          page={source.page}
          passage={source.passage}
          fermer={() => setSource(null)}
        />
      )}
    </Modale>
  )
}

const EXPLICATIONS: Record<string, string> = {
  GARDER_EXISTANT: 'La nouvelle information est rejetée, avec son motif. Elle reste consultable.',
  REMPLACER:
    'La donnée du dossier est archivée et remplacée. Son ancienne valeur reste consultable.',
  CONSERVER_LES_DEUX: 'Les deux informations restent validées dans le dossier.',
  EVOLUTION_DATEE:
    "Ce n'est pas une contradiction : la situation a changé. L'ancienne valeur est archivée et liée à la nouvelle.",
}

function Cote({
  titre,
  donnee,
  surSource,
}: {
  titre: string
  donnee: Donnee
  surSource: (s: { id: string; page: number | null; passage: string | null }) => void
}) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3">
      <div className="text-[11.5px] font-medium tracking-wide text-brume uppercase">{titre}</div>
      <p className="mt-1 text-[14px] font-medium">{donnee.libelle}</p>

      <dl className="mt-2 flex flex-col gap-1 text-[12.5px]">
        {detailLisible(donnee.detail).map((ligne) => (
          <div key={ligne.etiquette} className="flex gap-2">
            <dt className="text-brume">{ligne.etiquette}</dt>
            <dd className="text-encre">{ligne.valeur}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-2 border-t border-trait pt-2 text-[12px] text-brume">
        {donnee.sources.length > 0 ? (
          donnee.sources.map((s) => (
            <button
              key={s.id}
              className="lien-source block"
              onClick={() => surSource({ id: s.documentId, page: s.page, passage: s.passage })}
            >
              <FileSearch className="h-3.5 w-3.5" />
              {s.titre}
              {s.dateDocument ? ` — ${f.dateCourte(s.dateDocument)}` : ''}
            </button>
          ))
        ) : (
          <span className="flex items-center gap-1">
            <Hand className="h-3.5 w-3.5" />
            Saisie manuelle{donnee.creePar ? ` par ${donnee.creePar.nom}` : ''} le{' '}
            {f.dateCourte(donnee.creeLe)}
          </span>
        )}
      </div>
    </div>
  )
}
